import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import UserAuthToken from "users/auth/tokens/user-auth-token";
import UserAuthTokenSchema from "users/auth/tokens/user-auth-token-schema";
import UserInformationSchema from "users/information/_schemas/user-information-schema";
import UserInformation from "users/information/user-information";
import { z } from "zod";

export type AuthInfoJSON = z.infer<typeof AuthInfoSchema.AuthInfoJSONZ>;
export type AuthInfoParams = z.infer<typeof AuthInfoSchema.AuthInfoZ>;

export default class AuthInfoSchema {
  public static readonly AuthInfoJSONZ = z.object({
    authToken: UserAuthTokenSchema.UserAuthTokenJSONZ,
    userInformation: UserInformationSchema.UserInformationJSONZ,
  });

  public static readonly AuthInfoZ = z.object({
    authToken: z.instanceof(UserAuthToken),
    userInformation: z.instanceof(UserInformation),
  });

  public static validate(values: unknown): AuthInfoParams {
    return parseOrThrow(AuthInfoSchema.AuthInfoZ, values, {
      logError: true,
      schemaName: "AuthInfoSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): AuthInfoJSON {
    return parseOrThrow(AuthInfoSchema.AuthInfoJSONZ, json, {
      logError: true,
      schemaName: "AuthInfoSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is AuthInfoParams {
    try {
      AuthInfoSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AuthInfoJSON {
    try {
      AuthInfoSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): AuthInfoParams {
    const parsed = parseOrThrow(AuthInfoSchema.AuthInfoJSONZ, json, {
      logError: true,
      schemaName: "AuthInfoSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return AuthInfoSchema.codec.decode(parsed);
  }

  public static serialize(authInfoParams: AuthInfoParams): AuthInfoJSON {
    const parsed = AuthInfoSchema.validate(authInfoParams);
    return AuthInfoSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(AuthInfoSchema.AuthInfoJSONZ, AuthInfoSchema.AuthInfoZ, {
    decode: (authInfoJSON) => ({
      authToken: new UserAuthToken(UserAuthTokenSchema.deserialize(authInfoJSON.authToken)),
      userInformation: new UserInformation(UserInformationSchema.deserialize(authInfoJSON.userInformation)),
    }),
    encode: (authInfoParams) => ({
      authToken: (() => {
        const token = UserAuthTokenSchema.serialize(authInfoParams.authToken);
        if (token == null) throw new Error("Invalid authToken during serialization");
        return token;
      })(),
      userInformation: authInfoParams.userInformation.toJSON(),
    }),
  });
}
